import { Alert, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import PageShell from '../components/PageShell';

/**
 * Plain-language reference for the access model, written for someone who has
 * never seen the schema.
 *
 * It exists because the console cannot explain itself in tooltips alone: the
 * model is two independent axes plus two exceptions, and every screen only ever
 * shows one slice of it. The recurring support question — "they're an admin, why
 * can't they see this workspace?" — has an answer that no single screen is in a
 * position to give.
 *
 * Deliberately not generated from the backend. It mirrors
 * `seiki-backend/app/services/common/authz.py`, which is the authority; when a
 * rule changes there, change the words here. A page that silently drifts is
 * worse than no page, so every claim below is one an operator can check against
 * that module.
 */

const { Title, Paragraph, Text } = Typography;

const YES = <Text style={{ color: '#067647' }}>Yes</Text>;
const NO = <Text type="secondary">&mdash;</Text>;

type Right = {
  key: string;
  action: string;
  note?: string;
  viewer: boolean;
  contributor: boolean;
  admin: boolean;
};

/** The workspace axis, in the order `WORKSPACE_ROLE_ORDER` puts it: each role
 *  can do everything the one before it can, plus what its own row adds. */
const WORKSPACE_RIGHTS: Right[] = [
  {
    key: 'view',
    action: 'See the inventory and campaigns in this workspace',
    viewer: true,
    contributor: true,
    admin: true,
  },
  {
    key: 'export',
    action: 'Export reports',
    viewer: true,
    contributor: true,
    admin: true,
  },
  {
    key: 'edit',
    action: 'Build and edit campaigns, media plans and networks',
    note: 'Includes picking individual billboards.',
    viewer: false,
    contributor: true,
    admin: true,
  },
  {
    key: 'upload',
    action: 'Upload inventory',
    note: 'Agencies and brands: a contributor can. Media owners: admin only, because their uploads are visible beyond this workspace.',
    viewer: false,
    contributor: true,
    admin: true,
  },
  {
    key: 'delete',
    action: 'Delete inventory',
    note: 'Reversible, and only ever their own workspace’s uploads.',
    viewer: false,
    contributor: false,
    admin: true,
  },
  {
    key: 'gross',
    action: 'See gross contacts',
    note: 'Also depends on whether the customer bought the metric.',
    viewer: false,
    contributor: false,
    admin: true,
  },
  {
    key: 'members',
    action: 'Add, remove and re-role people in this workspace',
    viewer: false,
    contributor: false,
    admin: true,
  },
];

const RIGHT_COLUMNS: ColumnsType<Right> = [
  {
    title: 'Can they…',
    key: 'action',
    render: (_, row) => (
      <div>
        {row.action}
        {row.note && (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.note}
            </Text>
          </div>
        )}
      </div>
    ),
  },
  {
    title: 'Viewer',
    key: 'viewer',
    width: 110,
    align: 'center',
    render: (_, row) => (row.viewer ? YES : NO),
  },
  {
    title: 'Contributor',
    key: 'contributor',
    width: 120,
    align: 'center',
    render: (_, row) => (row.contributor ? YES : NO),
  },
  {
    title: 'Admin',
    key: 'admin',
    width: 110,
    align: 'center',
    render: (_, row) => (row.admin ? YES : NO),
  },
];

type Where = { key: string; fact: string; screen: string };

const WHERE_TO_CHANGE: Where[] = [
  { key: 'org', fact: 'Org role', screen: 'Users → click a user → Roles' },
  { key: 'staff', fact: 'Seiki staff', screen: 'Users → click a user → Roles' },
  {
    key: 'wsrole',
    fact: 'Workspace role',
    screen: 'Users → click a user → Workspaces, or the Workspaces screen',
  },
  {
    key: 'membership',
    fact: 'Adding or removing a workspace membership',
    screen: 'Workspaces → click a workspace → Add member',
  },
  { key: 'grant', fact: 'A capability grant', screen: 'Accesses' },
  {
    key: 'pages',
    fact: 'Product scope (pages, metrics, charts, campaign types, inventory fields)',
    screen: 'Organizations → click an organization',
  },
  {
    key: 'branding',
    fact: 'Logo, logomark, favicon and email banner of a whitelabel site',
    screen: 'Organizations → click an organization → Branding',
  },
  { key: 'status', fact: 'Account status, password reset', screen: 'Users → click a user' },
  {
    key: 'moveorg',
    fact: 'Which organization someone belongs to',
    screen: 'Users → click a user → Organization → Move',
  },
];

const WHERE_COLUMNS: ColumnsType<Where> = [
  { title: 'To change…', dataIndex: 'fact' },
  { title: 'Go to', dataIndex: 'screen' },
];

export default function DocumentationPage() {
  return (
    <PageShell
      title="Documentation"
      subtitle={
        <>
          What the roles in this console mean, in plain language. Written for anyone
          picking the console up for the first time.
        </>
      }
    >
      <div className="doc">
        <section>
          <Title level={4}>Start here: access answers two separate questions</Title>
          <Paragraph>
            Almost every confusion about permissions comes from treating these as one
            question. They are not related, and a person&apos;s answer to one says nothing
            about the other.
          </Paragraph>
          <Paragraph>
            <ol className="doc-list">
              <li>
                <strong>Where do they work, and what may they do there?</strong> This is
                their <strong>workspace role</strong>. A workspace is a working space
                holding inventory and campaigns. Someone can belong to several, with a
                different role in each &mdash; admin of one, viewer of another.
              </li>
              <li>
                <strong>What is their position in their own company?</strong> This is their{' '}
                <strong>org role</strong>. One per person. It decides whether they can run
                the company&apos;s account &mdash; create workspaces, invite colleagues
                &mdash; and nothing else.
              </li>
            </ol>
          </Paragraph>
          <Paragraph>
            Two things sit outside both: <strong>Seiki staff</strong>, for our own
            employees, and <strong>capability grants</strong>, which make a single
            exception for one person in one workspace. Both are explained below.
          </Paragraph>
        </section>

        <section>
          <Alert
            type="info"
            showIcon
            message="The one rule that surprises everyone"
            description={
              <>
                Being an <strong>org admin</strong> gives someone <em>no</em> access to any
                workspace&apos;s inventory or campaigns. If they are not a member of a
                workspace, they cannot see inside it &mdash; however senior they are in
                their company. Running the account and working in a workspace are separate
                permissions, and the fix for &ldquo;they can&apos;t see anything&rdquo; is
                usually a missing workspace membership, not a bigger role.
              </>
            }
          />
        </section>

        <section>
          <Title level={4}>Workspace role: what someone may do in one workspace</Title>
          <Paragraph>
            Three levels, each including everything the one before it can do. Set per
            workspace, so the same person can hold a different level in each.
          </Paragraph>
          <Paragraph>
            <Tag>Viewer</Tag> looks but does not change &nbsp;
            <Tag color="blue">Contributor</Tag> does the day-to-day work &nbsp;
            <Tag color="geekblue">Admin</Tag> runs the workspace, including who is in it
          </Paragraph>
          <Table<Right>
            rowKey="key"
            size="small"
            pagination={false}
            columns={RIGHT_COLUMNS}
            dataSource={WORKSPACE_RIGHTS}
          />
        </section>

        <section>
          <Title level={4}>Org role: what someone may do for their whole company</Title>
          <Paragraph>
            Two values, and only the first one grants anything.
          </Paragraph>
          <Paragraph>
            <strong>Org admin</strong> can create workspaces, see the list of the
            company&apos;s workspaces, invite people into the company, deactivate them,
            manage their roles and reset their passwords, and export the whole
            company&apos;s inventory.
          </Paragraph>
          <Paragraph>
            <strong>Org member</strong> can do none of those. This is the normal setting;
            most people should be an org member.
          </Paragraph>
          <Paragraph type="secondary">
            When a user&apos;s org role is shown as <strong>Not set</strong>, nothing
            company-wide is open to them at all. That is a value never filled in rather
            than a deliberate choice, and it is worth setting.
          </Paragraph>
        </section>

        <section>
          <Title level={4}>In the wrong company: move them, don&apos;t start over</Title>
          <Paragraph>
            Someone can be set up under the wrong organization &mdash; a typo in the
            invitation, a company that turned out to be two companies. Open the user and
            use <strong>Move to another organization</strong> rather than deleting the
            account and making a new one; deleting stops working as soon as anyone has
            used the account for anything.
          </Paragraph>
          <Paragraph>
            A move is a replacement, not an addition. Their memberships in the old
            company&apos;s workspaces are removed, any capability grants and pending
            access requests there are withdrawn, invitations they sent that nobody has
            accepted yet are cancelled. The new company applies to their very next action,
            and they are asked to sign in again within half an hour rather than being cut
            off mid-click. Their org role comes along unless you change it, and Seiki
            staff access &mdash; being nothing to do with any one company &mdash; is
            untouched. They also land on the new company&apos;s whitelabel, so their old
            sign-in bookmark stops working.
          </Paragraph>
          <Alert
            type="info"
            showIcon
            message="Some accounts cannot be moved, and the panel says so before you try"
            description="If the person owns inventory, campaigns, media plans or networks, those records are attached to them rather than to a workspace, and moving the account would carry one customer's work into another's. Deactivate that account and create a fresh one in the right organization instead."
          />
        </section>

        <section>
          <Title level={4}>Seiki staff: for our employees only</Title>
          <Paragraph>
            A switch, not a role. It leaves both roles above exactly as they are, and it
            lets that person do everything, in every customer&apos;s account &mdash;
            including workspaces they are not a member of. It is the one exception to the
            rule above.
          </Paragraph>
          <Paragraph>
            It also carries the two things nobody else can do at all: recalculating KPIs,
            and hard-deleting inventory.
          </Paragraph>
          <Alert
            type="warning"
            showIcon
            message="Never grant this to a customer"
            description="It reaches across every organization on the platform, not just theirs. It is for Seiki employees who need to work inside a customer's account."
          />
        </section>

        <section>
          <Title level={4}>Capability grants: one exception, one workspace</Title>
          <Paragraph>
            Sometimes a person needs one specific thing their role does not include &mdash;
            a viewer who must upload inventory, say. Rather than promoting them and handing
            over everything else, grant that single capability. A grant covers{' '}
            <strong>one person, in one workspace, for one capability</strong>, and can be
            set to expire.
          </Paragraph>
          <Paragraph>
            Five can be granted: uploading inventory, deleting inventory, editing networks,
            selecting individual billboards, and viewing gross contacts. Customers ask for
            these on the Accesses screen and staff approve them there; staff can also grant
            one directly when it was agreed elsewhere.
          </Paragraph>
          <Paragraph type="secondary">
            Company-wide powers cannot be granted this way &mdash; a grant applies to one
            workspace, so it has nothing to attach them to. Neither can recalculating KPIs.
          </Paragraph>
        </section>

        <section>
          <Title level={4}>Product scope: what the customer bought</Title>
          <Paragraph>
            A different question from everything above, and worth keeping separate: not{' '}
            <em>may this person do it</em>, but <em>did this customer buy that part of the
            product</em>. It applies to everyone in the organization at once, whatever their
            role, and it is set on the <strong>Organizations</strong> screen: click the
            organization&apos;s row.
          </Paragraph>
          <Paragraph>
            <ul className="doc-list">
              <li>
                <strong>Pages</strong> &mdash; Campaigns, Media Plans and Billboard Types can
                each be switched off. That hides the sidebar entry and the route.
              </li>
              <li>
                <strong>Metrics</strong> and <strong>charts</strong> &mdash; which KPIs and
                breakdowns appear. These apply to the PDF report and to CSV/Excel exports as
                well as to the screen, so unticking one removes it everywhere rather than
                just hiding it in the browser.
              </li>
              <li>
                <strong>Campaign types</strong> &mdash; which kinds of campaign they may
                create.
              </li>
              <li>
                <strong>Inventory fields</strong> &mdash; which billboard attributes show in
                the inventory panels.
              </li>
              <li>
                <strong>Inventory reference sheet</strong> &mdash; an optional link to the
                customer&apos;s own spreadsheet, shown as a banner on My Inventory.
              </li>
            </ul>
          </Paragraph>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="&ldquo;All&rdquo; is not the same as ticking every box"
            description={
              <>
                For metrics, charts and campaign types, <strong>All</strong> means
                unrestricted: whatever the platform adds next month appears for them
                automatically. Switching to <strong>Only selected</strong> and ticking
                everything looks identical today and is not the same thing &mdash; it freezes
                the organization at today&apos;s list, and the next metric Seiki ships stays
                hidden from them until someone comes back and ticks it.
              </>
            }
          />
          <Paragraph>
            The rest of the sidebar &mdash; Dashboard, My Inventory, Scenario Planning,
            Networks, FAQ, Users &mdash; cannot be switched off. Those follow the role, the
            same way for every customer: Scenario Planning is hidden from external users,
            Billboard Types is admin-only, and so on. If something is missing for one person
            but not their colleague, it is a role question; if it is missing for everyone in
            the company, it is this one.
          </Paragraph>
          <Paragraph type="secondary">
            The organization is the only thing that holds any of this. It used to live on the
            whitelabel tenant, in a git config file: that could say nothing about an
            organization on the bare Seiki domain, which has no tenant, it mixed branding with
            what the customer bought, and changing it needed a pull request. Existing
            organizations were given whatever their tenant said at the time. The tenant now
            carries branding only &mdash; colours and terminology.
          </Paragraph>
        </section>

        <section>
          <Title level={4}>Careful: &ldquo;admin&rdquo; means three different things</Title>
          <Paragraph>
            This is the single biggest source of mistakes, and it is worth reading twice.
          </Paragraph>
          <Paragraph>
            <ul className="doc-list">
              <li>
                <strong>Workspace admin</strong> &mdash; runs one workspace. Nothing beyond
                it.
              </li>
              <li>
                <strong>Org admin</strong> &mdash; runs a company&apos;s account. No access
                to the contents of any workspace.
              </li>
              <li>
                <strong>Admin</strong>, unqualified &mdash; a label the customer app shows,
                worked out from the two above. Nobody sets it, here or anywhere; it is a
                description, not a permission. It is not shown in this console for exactly
                that reason.
              </li>
            </ul>
          </Paragraph>
          <Paragraph>
            When someone says &ldquo;make them an admin&rdquo;, the useful question back is{' '}
            <em>admin of what</em> &mdash; one workspace, or the company&apos;s account?
          </Paragraph>
        </section>

        <section>
          <Title level={4}>Where to change each thing</Title>
          <Table<Where>
            rowKey="key"
            size="small"
            pagination={false}
            columns={WHERE_COLUMNS}
            dataSource={WHERE_TO_CHANGE}
          />
        </section>

        <section>
          <Title level={5} type="secondary">
            If you are reading the API or the database
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            The org role is <span className="mono">users.org_role</span> (
            <span className="mono">org_admin</span> or{' '}
            <span className="mono">org_member</span>), Seiki staff is{' '}
            <span className="mono">users.is_seiki_staff</span>, and the workspace role is{' '}
            <span className="mono">workspace_users.role</span> (
            <span className="mono">workspace_viewer</span>,{' '}
            <span className="mono">workspace_contributor</span>,{' '}
            <span className="mono">workspace_admin</span>). The unqualified label appears as{' '}
            <span className="mono">effective_role</span> and is computed on read &mdash; an
            org admin is <span className="mono">admin</span>, and anyone else takes the
            label their workspace role implies. It is stored nowhere; the column that used
            to hold it was dropped precisely because it drifted from the two facts it was
            copied from.
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            Product scope is six columns on <span className="mono">organizations</span>, served
            to the customer app as <span className="mono">org_features</span> on{' '}
            <span className="mono">/auth/me</span>. Block-lists (
            <span className="mono">disabled_modules</span>,{' '}
            <span className="mono">hidden_inventory_fields</span>) are not-null, and{' '}
            <span className="mono">[]</span> hides nothing. Allow-lists (
            <span className="mono">campaign_types</span>,{' '}
            <span className="mono">visible_metrics</span>,{' '}
            <span className="mono">visible_charts</span>) use <span className="mono">null</span>{' '}
            for unrestricted &mdash; that is the &ldquo;All&rdquo; above, and it is why it
            differs from an exhaustive list. Resolved in{' '}
            <span className="mono">app/core/org_features.py</span>. There is no fallback and no
            second source: <span className="mono">tenants.features</span> was the previous home
            and the column is dropped, so a config file still carrying it now fails to load
            rather than being ignored.
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 13 }}>
            Every rule on this page is enforced in one place:{' '}
            <span className="mono">app/services/common/authz.py</span> in{' '}
            <span className="mono">seiki-backend</span>. That module is the authority &mdash;
            if it disagrees with this page, it is right and this page needs editing.
          </Paragraph>
        </section>
      </div>
    </PageShell>
  );
}
